import startCase from 'lodash/startCase'

export const PROMOTIONS_API_IDENTIFIER = 'promotions'
export const DEFAULT_ERROR_MESSAGE = 'Whoops, there was a problem...'
export const POLL_INTERVAL_MS = 30 * 1000
export const TWENTY_FOUR_H_MS = 24 * 60 * 60 * 1000

/**
 * Matches the bulk-import CSV template (column names and sample rows).
 * Column semantics align with product documentation (conditions/discounts as JSON arrays, etc.).
 * JSON in cells uses doubled quotes inside quoted fields (standard CSV).
 * The header row may end with a trailing comma (empty final column), matching Excel/Numbers exports.
 */
export const PROMOTIONS_BULK_IMPORT_CSV_EXAMPLE = `action,identifier,name,status,priority,customerGroup,conditions,discounts,maximumUses,maximumUsesPerEmail,startDateTime,endDateTime,applicationSuccessMessage,orderTransferCode,promotionCodes,
create,summer-sale-2026,Summer Sale 10% Off,active,10,[],"[{""type"":""cart_total"",""amount"":10000}]","[{""type"":""percentage"",""amount"":1000}]",500,1,2026-06-01 00:00:00,2026-08-31 23:59:59,Summer discount applied,SUMMER10,"[{""code"":""SUMMER10"",""maximumUses"":500,""maximumUsesPerEmail"":1},{""code"":""TRADE10"",""maximumUses"":50,""maximumUsesPerEmail"":1}]",
create,vip-fixed-2026,VIP Fixed Discount,active,20,"[""vip""]",[],"[{""type"":""fixed"",""amount"":500}]",100,1,,,VIP discount applied,VIP20,"[{""code"":""VIPONLY"",""maximumUses"":100,""maximumUsesPerEmail"":1}]",
amend,winter-clearance-2025,Winter Clearance (Extended),active,5,[],[],[],,,2026-01-01 00:00:00,2026-03-31 23:59:59,Winter discount applied,WINTER25,[],
delete,expired-promo-2024,,,,,,,,,,,,,,`

/** What each column means (same order as the header row above). Matches API documentation. */
export const PROMOTIONS_IMPORT_COLUMN_GUIDE = [
    {
        column: 'action',
        required: true,
        format: 'String',
        description: 'One of: create, amend, delete.',
        example: 'create',
    },
    {
        column: 'identifier',
        required: true,
        format: 'String',
        description:
            'Unique identifier for the promotion; must be non-empty. Must be unique when creating a new promotion.',
        example: 'test-promo-10off',
    },
    {
        column: 'name',
        required: false,
        format: 'String',
        description: 'Promotion name (display label).',
        example: '10% Off Orders Over £100',
    },
    {
        column: 'status',
        required: false,
        format: 'String',
        description: 'Promotion status (e.g. active or archived).',
        example: 'active',
    },
    {
        column: 'priority',
        required: false,
        format: 'Integer',
        description:
            'Numeric priority. Higher values are evaluated first when multiple promotions could apply.',
        example: '10',
    },
    {
        column: 'customerGroup',
        required: false,
        format: 'JSON array of strings',
        description:
            'JSON array of string codes for customer groups this promotion applies to (not objects). Use [] when there is no group restriction.',
        example: '["trade","vip"] or []',
    },
    {
        column: 'conditions',
        required: false,
        format: 'JSON array of objects',
        description:
            'JSON array of objects describing rules that must be met for the promotion to trigger (not an array of plain strings). Use [] when none.',
        example: '[{"type":"cart_total","amount":10000}] or []',
    },
    {
        column: 'discounts',
        required: false,
        format: 'JSON array of objects',
        description:
            'JSON array of objects describing discounts applied when the promotion triggers (not an array of plain strings). Use [] when none.',
        example: '[{"type":"percentage","amount":1000}] or []',
    },
    {
        column: 'maximumUses',
        required: false,
        format: 'Integer or empty',
        description: 'Maximum total redemptions across all customers. Leave empty for no cap.',
        example: '500 or empty',
    },
    {
        column: 'maximumUsesPerEmail',
        required: false,
        format: 'Integer or empty',
        description: 'Maximum uses per customer (per email). Leave empty for no per-customer cap.',
        example: '1 or empty',
    },
    {
        column: 'startDateTime',
        required: false,
        format: 'YYYY-MM-DD HH:MM:SS',
        description:
            'Promotion start. Use the documented date-time format (match the format from a promotions export).',
        example: '2026-05-01 00:00:00',
    },
    {
        column: 'endDateTime',
        required: false,
        format: 'YYYY-MM-DD HH:MM:SS',
        description: 'Promotion end. Leave empty if open-ended.',
        example: '2026-08-31 23:59:59 or empty',
    },
    {
        column: 'applicationSuccessMessage',
        required: false,
        format: 'String',
        description: 'Message shown to the customer when the promotion is successfully applied.',
        example: 'Discount applied successfully',
    },
    {
        column: 'orderTransferCode',
        required: false,
        format: 'String',
        description:
            'Code passed to Kerridge on order transfer for this promotion. Leave empty if not used.',
        example: 'TRANSFER10',
    },
    {
        column: 'promotionCodes',
        required: false,
        format: 'JSON array of objects',
        description:
            'JSON array of voucher code objects. Each object must have a "code" (string), "maximumUses" (integer, defaults to 1), and "maximumUsesPerEmail" (integer, defaults to 1). Use [] if the promotion does not use voucher codes.',
        example:
            '[{"code":"SUMMER10","maximumUses":500,"maximumUsesPerEmail":1}] or []',
    },
]

/** What each action value does. */
export const PROMOTIONS_IMPORT_ACTION_GUIDE = [
    {
        action: 'create',
        description: 'Creates a new promotion. The identifier must not already exist.',
    },
    {
        action: 'amend',
        description:
            'Updates an existing promotion. The identifier must match an existing promotion.',
    },
    {
        action: 'delete',
        description:
            'Deletes an existing promotion. The identifier must match an existing promotion.',
    },
]

/** Each option may set `exampleCsv`: `{ content, download }` for an inline sample, or
 *  `{ href, download }` when `href` is a full URL. Omit `exampleCsv` to hide the link. */
export const PROMOTIONS_IMPORT_MANAGEMENT_OPTIONS = [
    {
        value: PROMOTIONS_API_IDENTIFIER,
        selectLabel: 'Bulk Manage Promotions',
        listTitle: 'Bulk Manage Promotions',
        historyTitle: 'Bulk Manage Promotions History',
        noResultsMessage:
            'No promotions imports yet. Upload a CSV containing the promotions data to manage.',
        uploadSuccessMessage: 'Promotions import queued successfully.',
        instructionsBeforeGuide:
            'Upload a CSV to bulk create, amend, or delete promotions. See the ',
        guideLinkLabel: 'promotions import guide',
        instructionsAfterGuide:
            ' for column and action reference. You can generate a Promotions Export, edit that file, and re-import it. Use Download CSV to retrieve the uploaded file for each import.',
        exampleCsv: {
            content: PROMOTIONS_BULK_IMPORT_CSV_EXAMPLE,
            download: 'example-bulk-promotions.csv',
        },
    },
]

export const getPromotionsImportManagementOption = (value) =>
    PROMOTIONS_IMPORT_MANAGEMENT_OPTIONS.find((o) => o.value === value) ??
    PROMOTIONS_IMPORT_MANAGEMENT_OPTIONS[0]

/** Build a `data:` URL for an inline example CSV (used by Manage Data “example here” link). */
export function exampleCsvDataHref(csvText) {
    return 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvText)
}

export const importLogId = (row) => row?.originId ?? row?.id

export const formatPromotionsDataTypeLabel = (value) => {
    if (value === PROMOTIONS_API_IDENTIFIER) return 'Promotions'
    return startCase((value || '').replaceAll('_', ' '))
}

export const isCreatedWithinLast24Hours = (createdAt) => {
    if (!createdAt) return false
    const t = new Date(createdAt).getTime()
    if (Number.isNaN(t)) return false
    return t >= Date.now() - TWENTY_FOUR_H_MS
}

export function axiosHydraMessage(err) {
    const d = err?.response?.data
    if (!d) return null
    if (Object.prototype.hasOwnProperty.call(d, 'hydra:description')) {
        return d['hydra:description']
    }
    if (Array.isArray(d.violations) && d.violations.length > 0) {
        return d.violations
            .map((v) => v.message)
            .filter(Boolean)
            .join(' ')
    }
    return null
}
